import React, { useEffect } from 'react';
import rtlPlugin from 'stylis-plugin-rtl';
import { StyleSheetManager } from 'styled-components';
import Script from 'next/script';
import { ModelManager } from '@adobe/aem-spa-page-model-manager';
import { useNextMiApolloClient, getDeployedEnvType } from '@marriott/mi-apollo-client-utils';
import { defineComponentMapping } from '../import-components';
import { CustomModelClient, canUseDOM, UserSessionProvider } from '@marriott/mi-headless-utils';
import { ApolloProvider } from '@apollo/client';
import path from 'path';
import { MockApolloClient } from '../mocks/apolloClient';
import {
  ErrorHandlerAlert,
  useLocaleStore,
  useAuthStore,
  useMediaQueryStore,
  useHeaderFieldsStore,
  DEFAULT_LANG,
  useDttStore,
  isAllowedForLocale,
} from '@marriott/mi-groups-components';
import { version as globalStylesVersion } from '@marriott/global-styles/package.json';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import '../styles/styles.css';
import { baseVariables } from '@marriott/mi-ui-library';
import { useRouter } from 'next/router';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';
path.resolve('./next.config.js');

declare global {
  interface Window {
    jQuery: unknown;
    $: unknown;
    deployedEnvType: string;
  }
}

if (canUseDOM) window.$ = window.jQuery = require('jquery-slim/dist/jquery.slim.min.js');

const mockUXL = process.env.NEXT_PUBLIC_API_MOCKING === 'true';

if (mockUXL) {
  require('../mocks');
}

const modelClient = new CustomModelClient(process.env.NEXT_PUBLIC_AEM_HOST);

if (process.env.NODE_ENV !== 'test') {
  ModelManager.initializeAsync({
    modelClient,
  });
}

const App = function (props) {
  const router = useRouter();
  const MiApolloClient = useNextMiApolloClient([], getDeployedEnvType());

  const { Component, pageProps } = props;
  const {
    model,
    pagePath,
    isAuthorMode,
    currentLocale,
    dttFlags = {},
    isDTT: isDttEnabledFromPreprocessor,
    cookies,
    apolloEnvVars = {},
    serverENV,
    requestId,
    headersData,
    NEXT_GLOBAL_ASSET_PREFIX,
    GOOGLE_MAP_API_KEY,
  } = pageProps;

  const serverEnvVars = serverENV ? JSON.parse(serverENV) : {};
  const locale = currentLocale ? currentLocale.replace('_', '-') : DEFAULT_LANG;

  const allowedComponents = model?.cqItems?.root?.[':items']?.responsivegrid?.allowedComponents?.components;

  const configureRtlPlugin = (...args: unknown[]) => {
    return rtlPlugin(args[0] as number, args[1] as string);
  };

  const enableRtl = pageProps?.model?.rtlEnable === 'true';

  // load global styles on the basis of dir present in props
  if (enableRtl) {
    import('@marriott/global-styles/dist/marriot.global.rtl.css');
  } else {
    import('@marriott/global-styles/dist/marriot.global.css');
  }

  if (!dttFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_DTT_GROUP]) {
    dttFlags[FEATURE_FLAGS.EXPERIENCE_MCOM_DTT_GROUP] = isDttEnabledFromPreprocessor;
  }

  const isRoomingListPage = router.asPath.split('/').includes('roominglist');

  let dttFlagForComponentMapping = dttFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_DTT_GROUP];
  if (canUseDOM) {
    dttFlagForComponentMapping = isRoomingListPage
      ? useDttStore.getState().isDtt
        ? useDttStore.getState().isDtt
        : dttFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_DTT_GROUP]
      : dttFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_DTT_GROUP];
  }

  defineComponentMapping(
    allowedComponents,
    pagePath,
    isAuthorMode,
    currentLocale,
    dttFlagForComponentMapping,
    enableRtl
  );

  const envVars = {
    ...apolloEnvVars,
    ...serverEnvVars,
  };

  envVars['GOOGLE_MAP_API_KEY'] = GOOGLE_MAP_API_KEY;

  if (canUseDOM && isRoomingListPage && useDttStore.getState().isDtt) {
    useDttStore.getState().setBrandedLogoFlag(!!dttFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_LOGO_AUTOMATION]);
  } else {
    useDttStore.getState().setDttFlags(dttFlags);
  }

  if (!Object.keys(useClientEnvVarsStore.getState().envVarsObject).length) {
    useClientEnvVarsStore.getState().setEnvVars(envVars);
  }

  if (!useLocaleStore.getState().locale) {
    useLocaleStore.getState().setLocale(locale);
  }

  if (useLocaleStore.getState().rtl !== enableRtl) {
    useLocaleStore.getState().setRtl(enableRtl);
  }

  if (canUseDOM) {
    useMediaQueryStore
      .getState()
      .setIsTabletAndAbove(window.innerWidth >= parseInt(baseVariables.mediaQuery.md.match(/\d+/)?.[0] as string));
  }

  if (canUseDOM) {
    const url = new URL(window.location.href);
    if (isRoomingListPage && useDttStore.getState().isDtt && !url.searchParams.has('dtt')) {
      url.searchParams.set('dtt', 'true');
      window.history.replaceState({}, '', url.toString());
    }
  }

  useEffect(() => {
    // TODO: added a temporary fallback to populate UserIdToken as the current env is not setting this cookie.
    if (!cookies.UserIdToken) {
      cookies.UserIdToken = cookies.associateToken || cookies.authStateToken;
    }
    useAuthStore.getState().setAuthCookie(cookies);
    useHeaderFieldsStore.getState().setRequestId(requestId);
    headersData.api && useHeaderFieldsStore.getState().setApiValue(headersData.api);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isAuthorMode) {
      let remoteURL: URL | string;
      try {
        remoteURL = new URL(document.body.dataset.remoteUrl || '');
        remoteURL = remoteURL.origin;
      } catch (e) {
        remoteURL = '';
      }
      const assetUrl = `${
        process.env['NEXT_PUBLIC_ASSET_DOMAIN'] || remoteURL || 'https://www.marriott.com'
      }${NEXT_GLOBAL_ASSET_PREFIX}`;

      const { asPath } = router;
      const path = asPath.split('/').filter(Boolean);
      const lastSegment = path[path.length - 1];

      const brandCodeMatch = lastSegment?.match(/brandCode=([^&]+)/);
      const brandCode = brandCodeMatch ? brandCodeMatch[1] : '';

      const globalStyle = document.createElement('link');

      globalStyle.rel = 'stylesheet';
      globalStyle.href =
        assetUrl +
        (enableRtl
          ? `global-styles/${globalStylesVersion}/marriot.global.rtl.css`
          : `global-styles/${globalStylesVersion}/marriot.global.css`);
      document.head.appendChild(globalStyle);

      if (brandCode) {
        const brandLink = document.createElement('link');

        brandLink.rel = 'stylesheet';
        brandLink.href = assetUrl + `brand-framework/brand-config.${brandCode}.css`;

        document.head.appendChild(brandLink);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  Object.defineProperty(configureRtlPlugin, 'name', { value: 'rtlPluginMonorepo' });

  return (
    <ErrorHandlerAlert>
      <UserSessionProvider pageProps={pageProps}>
        <StyleSheetManager stylisPlugins={enableRtl ? [configureRtlPlugin] : []}>
          <ApolloProvider client={mockUXL ? MockApolloClient : MiApolloClient}>
            <Component {...pageProps} />
            {isAllowedForLocale(locale) && (
              <>
                <Script src={`https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAP_API_KEY}`} defer={true} />
                <Script src="https://unpkg.com/@googlemaps/markerwithlabel@1.0.3/dist/index.min.js" defer={true} />
              </>
            )}
          </ApolloProvider>
        </StyleSheetManager>
      </UserSessionProvider>
    </ErrorHandlerAlert>
  );
};

export default App;
